import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { IAddressForm } from '@/models/address';
import { googlePlaceService } from '@/services/service.google';
import {
  ParsedIDCard,
  PERSON_FORM_FIELD_MAPPING,
  parseHeightToExact,
} from '@/utils/idCardParser';
import {
  buildScannedAddress,
  buildScannedAddressString,
} from '@/utils/scannedAddress';

export const VERIFIABLE_FIELDS = [
  'driverLicense.state',
  'driverLicense.number',
  'driverLicense.vehicleClass',
  'driverLicense.restrictionCodes',
  'driverLicense.endorsementCodes',
  'driverLicense.expireDate',
  'driverLicense.documentNumber',
  'driverLicense.issueDate',
  // Identity fields
  'firstName',
  'lastName',
  'middleName',
  'suffix',
  'birthday',
  'sex',
  'physicalCharacteristics.eyeColor',
  'physicalCharacteristics.heightExact.feet',
  'physicalCharacteristics.heightExact.inches',
  'addresses.0.addressNumber',
  'addresses.0.completeStreetName',
  'addresses.0.subAddress',
  'addresses.0.placeName',
  'addresses.0.state',
  'addresses.0.zipcode',
  'addresses.0.country',
] as const;

interface VerifiedFieldsContextType {
  /**
   * Original values from the scan - used to determine if a field is still verified
   */
  scannedValues: Record<string, any>;

  /**
   * Check if a field is currently verified
   * A field is verified if it has a scanned value AND current value matches scanned value
   */
  isFieldVerified: (fieldName: string) => boolean;

  /**
   * Apply scanned data to the form and track verified fields
   */
  applyScannedData: (selectedFields: Partial<ParsedIDCard>) => void;

  /**
   * Check if any fields are currently verified
   */
  hasVerifiedFields: boolean;

  /**
   * Get the list of currently verified field names
   */
  getVerifiedFields: () => string[];
  getVerifiedFieldsWithValues: () => Record<string, any>;
  initializeFromSavedData: (savedVerifiedFields: Record<string, any>) => void;
}

const VerifiedFieldsContext = createContext<VerifiedFieldsContextType | null>(
  null,
);

interface VerifiedFieldsProviderProps {
  children: ReactNode;
  initialVerifiedFields?: Record<string, any>;
}

export const VerifiedFieldsProvider: FC<VerifiedFieldsProviderProps> = ({
  children,
  initialVerifiedFields,
}) => {
  const { setValue, getValues, control } = useFormContext();

  const [scannedValues, setScannedValues] = useState<Record<string, any>>(
    initialVerifiedFields || {},
  );

  useEffect(() => {
    if (
      initialVerifiedFields &&
      Object.keys(initialVerifiedFields).length > 0
    ) {
      setScannedValues(initialVerifiedFields);
    }
  }, [initialVerifiedFields]);

  const watchedValues = useWatch({
    control,
    name: VERIFIABLE_FIELDS as unknown as string[],
  });

  const currentValues = useMemo(() => {
    const values: Record<string, any> = {};
    VERIFIABLE_FIELDS.forEach((field, index) => {
      values[field] = watchedValues[index];
    });
    return values;
  }, [watchedValues]);

  /**
   * Check if a field is verified by comparing current value to scanned value
   * Returns true only if:
   * 1. The field has a scanned value
   * 2. The current value matches the scanned value
   */
  const isFieldVerified = useCallback(
    (fieldName: string): boolean => {
      const scannedValue = scannedValues[fieldName];

      if (scannedValue === undefined) {
        return false;
      }

      const currentValue = currentValues[fieldName];

      const normalizeValue = (val: any): string => {
        if (val === null || val === undefined || val === '') {
          return '';
        }
        return String(val).trim().toUpperCase();
      };

      return normalizeValue(currentValue) === normalizeValue(scannedValue);
    },
    [scannedValues, currentValues],
  );

  /**
   * Apply scanned data to form fields and store scanned values for verification
   */
  const applyScannedData = useCallback(
    async (selectedFields: Partial<ParsedIDCard>) => {
      const newScannedValues: Record<string, any> = { ...scannedValues };
      let hasAddressFields = false;

      setValue('isVerified', true, {
        shouldDirty: true,
        shouldTouch: true,
      });

      Object.entries(selectedFields).forEach(([parsedKey, value]) => {
        if (value === undefined || value === null || value === '') return;

        if (parsedKey === 'height') {
          const parsedHeight = parseHeightToExact(String(value));
          if (!parsedHeight) return;

          setValue(
            'physicalCharacteristics.heightExact.feet',
            parsedHeight.feet,
            {
              shouldDirty: true,
              shouldTouch: true,
            },
          );
          setValue(
            'physicalCharacteristics.heightExact.inches',
            parsedHeight.inches,
            {
              shouldDirty: true,
              shouldTouch: true,
            },
          );

          newScannedValues['physicalCharacteristics.heightExact.feet'] =
            parsedHeight.feet;
          newScannedValues['physicalCharacteristics.heightExact.inches'] =
            parsedHeight.inches;
          return;
        }

        const formFieldPath =
          PERSON_FORM_FIELD_MAPPING[parsedKey as keyof ParsedIDCard];
        if (!formFieldPath) return;

        if (formFieldPath.startsWith('addresses.0.')) {
          hasAddressFields = true;
          return;
        }

        setValue(formFieldPath, value, {
          shouldDirty: true,
          shouldTouch: true,
        });

        newScannedValues[formFieldPath] = value;
      });

      if (hasAddressFields) {
        const addressString = buildScannedAddressString(selectedFields);

        if (addressString) {
          let geocoded: IAddressForm | null = null;
          try {
            geocoded = await googlePlaceService.geocodeAddress(addressString);
          } catch {
            geocoded = null;
          }

          const builtAddress = buildScannedAddress(selectedFields, geocoded);
          if (builtAddress) {
            // The address row is rendered by a Controller registered at the
            // whole-object path; its children are not registered inputs, so
            // child-path setValue calls never reach it — the row must be
            // written as one object.
            const currentAddress = getValues('addresses.0') ?? {};
            setValue(
              'addresses.0',
              { ...currentAddress, ...builtAddress },
              { shouldDirty: true, shouldTouch: true },
            );

            if (geocoded) {
              Object.entries(builtAddress).forEach(([key, value]) => {
                newScannedValues[`addresses.0.${key}`] = value;
              });
            } else {
              // Without geocoding only the scanned document values count as
              // verified — the derived defaults (country, label, zipName…)
              // do not.
              const rawScannedKeys: [keyof ParsedIDCard, keyof IAddressForm][] =
                [
                  ['addressNumber', 'addressNumber'],
                  ['streetAddress1', 'completeStreetName'],
                  ['streetAddress2', 'subAddress'],
                  ['city', 'placeName'],
                  ['addressState', 'state'],
                  ['zipCode', 'zipcode'],
                  ['country', 'country'],
                ];
              rawScannedKeys.forEach(([scannedKey, addressKey]) => {
                if (selectedFields[scannedKey]) {
                  newScannedValues[`addresses.0.${addressKey}`] =
                    builtAddress[addressKey];
                }
              });
            }
          }
        }
      }

      setScannedValues(newScannedValues);
    },
    [setValue, getValues, scannedValues],
  );

  /**
   * Check if any fields have been verified by scan
   */
  const hasVerifiedFields = useMemo(() => {
    return VERIFIABLE_FIELDS.some((field) => isFieldVerified(field));
  }, [isFieldVerified]);

  /**
   * Get list of all currently verified fields
   */
  const getVerifiedFields = useCallback((): string[] => {
    return VERIFIABLE_FIELDS.filter((field) => isFieldVerified(field));
  }, [isFieldVerified]);

  const getVerifiedFieldsWithValues = useCallback((): Record<string, any> => {
    const result: Record<string, any> = {};
    Object.entries(scannedValues).forEach(([fieldName, scannedValue]) => {
      if (isFieldVerified(fieldName)) {
        result[fieldName] = scannedValue;
      }
    });
    return result;
  }, [scannedValues, isFieldVerified]);

  const initializeFromSavedData = useCallback(
    (savedVerifiedFields: Record<string, any>) => {
      if (savedVerifiedFields && Object.keys(savedVerifiedFields).length > 0) {
        setScannedValues(savedVerifiedFields);
      }
    },
    [],
  );

  const contextValue = useMemo(
    () => ({
      scannedValues,
      isFieldVerified,
      applyScannedData,
      hasVerifiedFields,
      getVerifiedFields,
      getVerifiedFieldsWithValues,
      initializeFromSavedData,
    }),
    [
      scannedValues,
      isFieldVerified,
      applyScannedData,
      hasVerifiedFields,
      getVerifiedFields,
      getVerifiedFieldsWithValues,
      initializeFromSavedData,
    ],
  );

  return (
    <VerifiedFieldsContext.Provider value={contextValue}>
      {children}
    </VerifiedFieldsContext.Provider>
  );
};

/**
 * Hook to access verified fields context
 * Must be used within a VerifiedFieldsProvider
 */
export const useVerifiedFields = (): VerifiedFieldsContextType => {
  const context = useContext(VerifiedFieldsContext);

  if (!context) {
    throw new Error(
      'useVerifiedFields must be used within a VerifiedFieldsProvider',
    );
  }

  return context;
};
